import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL?.trim();
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();

export const cloudConfigured = Boolean(url && publishableKey);
export const walletRecoveryEnabled = cloudConfigured && import.meta.env.VITE_ENABLE_WALLET_RECOVERY === 'true';

export const supabase: SupabaseClient | null = cloudConfigured
  ? createClient(url!, publishableKey!, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null;

const PASSWORD_RECOVERY_EVENT = 'safe-wallet-password-recovery';
let passwordRecoveryPending = typeof window !== 'undefined'
  && (
    new URLSearchParams(window.location.search).get('password-recovery') === '1'
    || new URLSearchParams(window.location.hash.slice(1)).get('type') === 'recovery'
  );

function markPasswordRecoveryPending(): void {
  passwordRecoveryPending = true;
  window.dispatchEvent(new Event(PASSWORD_RECOVERY_EVENT));
}

if (supabase && typeof window !== 'undefined') {
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'PASSWORD_RECOVERY') markPasswordRecoveryPending();
  });
}

export function isPasswordRecoveryPending(): boolean {
  return passwordRecoveryPending;
}

export function subscribeToPasswordRecovery(callback: () => void): () => void {
  window.addEventListener(PASSWORD_RECOVERY_EVENT, callback);
  return () => window.removeEventListener(PASSWORD_RECOVERY_EVENT, callback);
}

export function clearPasswordRecoveryPending(): void {
  passwordRecoveryPending = false;
  const url = new URL(window.location.href);
  url.searchParams.delete('password-recovery');
  url.searchParams.delete('code');
  url.hash = '';
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
}

export async function currentAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session?.access_token ?? null;
}

export async function currentUserId(): Promise<string | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.auth.getUser();
  if (error) return null;
  return data.user?.id ?? null;
}
